import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit,
  FiCopy,
  FiFileText,
  FiPackage,
  FiDollarSign,
  FiClock,
  FiBox,
  FiInfo,
  FiLayers,
  FiCheckCircle
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useFormulaciones } from '../../hooks/useFormulaciones';

const FormulacionDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Hook de formulaciones
  const {
    getFormulacionById,
    isLoading: formulacionesLoading,
    error: formulacionesError
  } = useFormulaciones();

  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      loadFormulacion();
    }
  }, [id]);

  const loadFormulacion = async () => {
    setIsLoading(true);
    try {
      const result = await getFormulacionById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        setError(result.message || 'Error al cargar formulación');
      }
    } catch (error) {
      setError('Error de conexión al cargar formulación');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/system/formulaciones/${id}/editar`);
  };

  const handleBack = () => {
    navigate('/system/formulaciones');
  };

  const handleDuplicate = () => {
    console.log('Duplicar formulación:', id);
    // Implementar duplicación si es necesario
  };

  const handleGeneratePDF = () => {
    console.log('Generar PDF:', id);
    // Implementar generación de PDF
  };

  const calculateTotals = useCallback(() => {
    if (!formData || !formData.ingredients) {
      return {
        totalPercentage: 0,
        totalCost: 0,
        costWithSilica: 0,
        costPerUnit: 0
      };
    }

    const totalPercentage = formData.ingredients.reduce((sum, ing) =>
      sum + (parseFloat(ing.percentage) || 0), 0
    );

    const totalCost = formData.ingredients.reduce((sum, ing) => {
      const qty = parseFloat(ing.quantity_per_unit) || 0;
      const cost = parseFloat(ing.cost_per_unit) || 0;
      return sum + (qty * cost);
    }, 0);

    const costWithSilica = formData.includes_silica
      ? totalCost + parseFloat(formData.silica_cost || 0)
      : totalCost;

    const costPerUnit = formData.quantity_per_container > 0
      ? costWithSilica / formData.quantity_per_container
      : 0;

    return {
      totalPercentage,
      totalCost,
      costWithSilica,
      costPerUnit
    };
  }, [formData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(amount || 0);
  };

  const getContainerTypeLabel = (type) => {
    const types = {
      'CAPSULES': 'Cápsulas',
      'TEA_BAGS': 'Bolsas de Té',
      'BOTTLES': 'Botellas',
      'JARS': 'Frascos',
      'BOXES': 'Cajas',
      'SACHETS': 'Sobres',
      'BULK': 'Granel'
    };
    return types[type] || type;
  };

  const getCapsuleTypeLabel = (type) => {
    const types = {
      'VEGETARIAN': 'Vegetarianas',
      'GELATIN': 'Gelatina',
      'SIZE_00': 'Tamaño 00',
      'SIZE_0': 'Tamaño 0',
      'SIZE_1': 'Tamaño 1',
      'SIZE_2': 'Tamaño 2',
      'NOT_APPLICABLE': 'No Aplica'
    };
    return types[type] || type;
  };

  const getFormulaTypeLabel = (type) => {
    const types = {
      'BETEL': 'Betel Natural',
      'NATURALANDLAB': 'Natural & Lab',
      'EXCLUSIVE': 'Exclusiva'
    };
    return types[type] || type;
  };

  const getQuantityUnitLabel = (unit) => {
    const units = {
      'UNIT': 'Unidades',
      'GRAM': 'Gramos',
      'ML': 'Mililitros',
      'OZ': 'Onzas'
    };
    return units[unit] || unit;
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando formulación..." />;
  }

  if (error || !formData) {
    return (
      <div className="factura-form-page">
        <div className="alert alert--danger">
          <strong>Error:</strong> {error || 'No se pudo cargar la formulación'}
        </div>
        <button className="btn btn--outline" onClick={handleBack}>
          <FiArrowLeft size={16} />
          Volver
        </button>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="factura-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleBack}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          Formulación #{id}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleGeneratePDF}>
            <FiFileText size={16} />
            PDF
          </button>
          <button className="btn btn--outline" onClick={handleDuplicate}>
            <FiCopy size={16} />
            Duplicar
          </button>
          <button className="btn btn--primary" onClick={handleEdit}>
            <FiEdit size={16} />
            Editar
          </button>
        </div>
      </div>

      {/* Card 1: Información Principal de la Formulación */}
      <div className="invoice-header-card">
        <div className="invoice-header-card__left">
          <div className="invoice-icon-circle">
            <FiLayers size={32} />
          </div>
          <div className="invoice-header-info">
            <h3 className="invoice-header-title">
              {formData.product_name}
            </h3>
            <p className="invoice-header-subtitle">
              {formData.company_name} • {getFormulaTypeLabel(formData.formula_type)}
            </p>
          </div>
        </div>

        <div className="invoice-header-card__grid">
          {/* Compañía */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiBox size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Compañía</label>
              <div className="invoice-field-value">{formData.company_name}</div>
            </div>
          </div>

          {/* Nombre del Producto */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiPackage size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Nombre del Producto</label>
              <div className="invoice-field-value">{formData.product_name}</div>
            </div>
          </div>

          {/* Tipo de Fórmula */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiLayers size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tipo de Fórmula</label>
              <div className="invoice-field-value">{getFormulaTypeLabel(formData.formula_type)}</div>
            </div>
          </div>

          {/* Tipo de Contenedor */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiBox size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tipo de Contenedor</label>
              <div className="invoice-field-value">{getContainerTypeLabel(formData.container_type)}</div>
            </div>
          </div>

          {/* Cantidad por Contenedor */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiPackage size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Cantidad por Contenedor</label>
              <div className="invoice-field-value">{formData.quantity_per_container}</div>
            </div>
          </div>

          {/* Unidad */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiInfo size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Unidad</label>
              <div className="invoice-field-value">{getQuantityUnitLabel(formData.quantity_unit)}</div>
            </div>
          </div>

          {/* Tipo de Cápsula - Solo si es cápsulas */}
          {formData.container_type === 'CAPSULES' && (
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiPackage size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Tipo de Cápsula</label>
                <div className="invoice-field-value">{getCapsuleTypeLabel(formData.capsule_type)}</div>
              </div>
            </div>
          )}

          {/* Tiempo de Mezcla */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiClock size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tiempo de Mezcla</label>
              <div className="invoice-field-value">{formData.mixing_time_minutes} minutos</div>
            </div>
          </div>

          {/* Mejor Uso */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiInfo size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Mejor Uso</label>
              <div className="invoice-field-value">{formData.best_use_date_years} años</div>
            </div>
          </div>

          {/* Incluir Sílica */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiDollarSign size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Sílica</label>
              <div className="invoice-field-value">
                {formData.includes_silica ? (
                  <span>
                    <FiCheckCircle size={16} style={{ color: '#28a745', marginRight: '4px' }} />
                    Incluida - {formatCurrency(formData.silica_cost)}
                  </span>
                ) : (
                  'No incluida'
                )}
              </div>
            </div>
          </div>

          {/* Notas - Ocupa 2 columnas */}
          {formData.notes && (
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiInfo size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas</label>
                <div className="invoice-field-value">{formData.notes}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card 2: Tabla de Ingredientes */}
      {formData.ingredients && formData.ingredients.length > 0 ? (
        <div className="invoice-table-card">
          <div className="invoice-table-header">
            <div className="table-header-icon">
              <FiLayers size={24} />
            </div>
            <div className="table-header-content">
              <h3 className="table-header-title">Ingredientes de la Formulación</h3>
              <p className="table-header-subtitle">{formData.ingredients.length} ingrediente(s)</p>
            </div>
          </div>

          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead className="invoice-table-head">
                <tr>
                  <th className="th-product">Ingrediente</th>
                  <th className="th-qty">Cantidad</th>
                  <th className="th-price">Unidad</th>
                  <th className="th-discount">%</th>
                  <th className="th-price">Costo/U</th>
                  <th className="th-total">Total</th>
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {formData.ingredients.map((ingredient, index) => {
                  const total = (ingredient.quantity_per_unit || 0) * (ingredient.cost_per_unit || 0);
                  return (
                    <tr key={index} className="invoice-table-row">
                      <td className="td-product">
                        <div className="product-cell">
                          <div className="product-cell-icon">
                            <FiPackage size={16} />
                          </div>
                          <div className="product-cell-info">
                            <div className="product-cell-name">{ingredient.ingredient_name}</div>
                            <div className="product-cell-code">{ingredient.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td-qty">
                        <span className="qty-badge">{ingredient.quantity_per_unit}</span>
                      </td>
                      <td className="td-price">
                        <span className="price-value">{ingredient.unit}</span>
                      </td>
                      <td className="td-discount">
                        <span className="discount-badge">{ingredient.percentage}%</span>
                      </td>
                      <td className="td-price">
                        <span className="price-value">{formatCurrency(ingredient.cost_per_unit)}</span>
                      </td>
                      <td className="td-total">
                        <span className="total-value">{formatCurrency(total)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="invoice-totals-section">
            <div className="totals-container">
              <div className="totals-row">
                <span className="totals-label">Porcentaje Total</span>
                <span className={`totals-value ${totals.totalPercentage !== 100 ? 'text-warning' : ''}`}>
                  {totals.totalPercentage.toFixed(2)}%
                </span>
              </div>
              <div className="totals-row">
                <span className="totals-label">Costo Ingredientes</span>
                <span className="totals-value">{formatCurrency(totals.totalCost)}</span>
              </div>
              {formData.includes_silica && (
                <div className="totals-row">
                  <span className="totals-label">Costo Sílica</span>
                  <span className="totals-value">{formatCurrency(formData.silica_cost)}</span>
                </div>
              )}
              <div className="totals-divider"></div>
              <div className="totals-row totals-row--final">
                <span className="totals-label">Costo Total por Lote</span>
                <span className="totals-value">{formatCurrency(totals.costWithSilica)}</span>
              </div>
              <div className="totals-row">
                <span className="totals-label">Costo por Unidad</span>
                <span className="totals-value">{formatCurrency(totals.costPerUnit)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="invoice-empty-state">
          <div className="empty-state-icon">
            <FiLayers size={64} />
          </div>
          <h3 className="empty-state-title">No hay ingredientes</h3>
          <p className="empty-state-message">
            Esta formulación no tiene ingredientes registrados
          </p>
        </div>
      )}
    </div>
  );
};

export default FormulacionDetalle;
